import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

import { brandText } from '../../../config/branding';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string;
  totalDebitAmount: number;
}

/**
 * revenue
 * @param {any} param0 - The param0
 * @param {any} param0.powerConsumedData - The powerConsumedData prop
 * @returns {any} The result
 */
function RevenueExpenseChart({
  powerConsumedData,
}: Readonly<{ powerConsumedData: ChartDataProperties[] }>) {
  const chartData = {
    labels: powerConsumedData.map((d) => d.label),
    datasets: [
      {
        label: 'Revenue',
        data: powerConsumedData.map((d) => d.totalDebitAmount),
        borderColor: '#F79009',
        /**
         * color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          const { chart } = context;
          const content = chart?.ctx;
          const gradient = content.createLinearGradient(0, 0, 0, chart.height);
          gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
          gradient.addColorStop(1, 'rgba(237, 237, 237, 0)');
          return gradient;
        },
        fill: 'origin', // Fills to the previous dataset
        tension: 0.4,
        borderWidth: 1,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const, // Explicitly cast to 'top' to ensure correct type
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
        padding: {
          right: 500,
        },
      },
      tooltip: {
        /**
         * color
         * @param {any} context - The context
         * @returns {any} The result
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            // Check the dataset label and apply different colors
            if (context.tooltipItems[0].dataset.label === 'Revenue') {
              return 'rgba(247, 144, 9, 0.9)'; // Orange for Revenue
            }
          }
          return 'rgba(0, 0, 0, 0.8)'; // Default color
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * title
           * @param {any} context - The context
           * @returns {any} The result
           */
          title: (context: any) => {
            // Set title based on dataset
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel === 'Revenue' ? 'Revenue' : '';
          },
          /**
           * label
           * @param {any} context - The context
           * @returns {any} The result
           */
          label: (context: any) => {
            // Format the label with currency
            return `${context?.raw.toLocaleString()} (${brandText.currency})`;
          },
        },
      },
    },

    interaction: {
      intersect: false, // Don't require an exact point to show the tooltip
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: false, // Hides the tick labels on the y-axis (revenue and expense values)
        },
        border: {
          display: false, // Hides the y-axis line
        },
      },
    },
  };

  return (
    <div style={{ width: '100%' }}>
      <Line
        data={chartData}
        options={options}
        height={250} // Explicit height for the chart
        width={900}
      />
    </div>
  );
}

export default RevenueExpenseChart;
